import { useState } from "react";
import Papa from "papaparse";

/**
 * Gemeinsamer Hook fuer die beiden Stammdaten-CsvImports (CatalogPage +
 * ArtikelPage). Beide Pages teilen die exakt gleiche CSV-Parse-Logik:
 * PapaParse mit Delimiter-Auto-Detect, Header-Detection, Synonym-basiertes
 * Auto-Mapping auf die Stammdaten-Felder. Page-spezifisches (Build-Row,
 * API-Call, Conflict-Strategie, Report-Anzeige, Dialog-Container) bleibt
 * in den Pages.
 */

export type CsvImportField =
  | "code"
  | "bezeichnung"
  | "kurzText"
  | "ean"
  | "vpe"
  | "einheit"
  | "basiseinheit"
  | "inhaltProVE"
  | "warengruppe"
  | "listenpreis"
  | "suchbegriffe";

/** Header-Synonyme — case-insensitive matched gegen die Header-Zeile. */
export const CSV_HEADER_SYNONYMS: Record<CsvImportField, string[]> = {
  code: ["code", "artikelcode", "artikelnr", "artikel-nr", "artnr", "art_nr"],
  bezeichnung: ["bezeichnung", "name", "artikelname", "title", "titel"],
  kurzText: ["kurztext", "kurz", "shorttext"],
  ean: ["ean", "gtin", "barcode"],
  vpe: ["vpe", "verpackung", "verpackungseinheit"],
  listenpreis: ["listenpreis", "preis", "price", "ek", "vk"],
  einheit: ["einheit", "unit", "me", "mengeneinheit"],
  basiseinheit: ["basiseinheit", "grundeinheit", "baseunit", "basis"],
  inhaltProVE: ["inhaltprove", "inhalt", "inhaltprove", "menge_je_ve", "inhaltjeve", "vpe_inhalt", "fuellmenge", "füllmenge"],
  warengruppe: ["warengruppe", "wg", "category", "kategorie", "gruppe"],
  suchbegriffe: ["suchbegriffe", "tags", "synonyme", "keywords"],
};

export type CsvImportMapping = Record<CsvImportField, string | null>;

/** Auto-mappt CSV-Header auf bekannte Felder durch Synonym-Suche.
 *  Kein Treffer → null. Mapping-Override geschieht spaeter im UI. */
export function csvAutoMap(headers: string[]): CsvImportMapping {
  const result: Partial<CsvImportMapping> = {};
  const keys = Object.keys(CSV_HEADER_SYNONYMS) as CsvImportField[];
  for (const k of keys) {
    const synonyms = CSV_HEADER_SYNONYMS[k].map((s) => s.toLowerCase());
    const match = headers.find((h) =>
      synonyms.includes(h.trim().toLowerCase())
    );
    result[k] = match ?? null;
  }
  return result as CsvImportMapping;
}

export interface UseCsvImportReturn {
  csvRows: Array<Record<string, string>> | null;
  headers: string[];
  mapping: CsvImportMapping | null;
  setMapping: React.Dispatch<React.SetStateAction<CsvImportMapping | null>>;
  parseError: string | null;
  setParseError: (msg: string | null) => void;
  dragging: boolean;
  setDragging: (b: boolean) => void;
  parseFile: (file: File) => void;
  handleFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  reset: () => void;
}

export interface UseCsvImportOptions {
  /** Wird ganz am Anfang von parseFile aufgerufen, bevor State gereset
   *  wird. Page kann hier eigene States (z.B. importError, report) clearen. */
  onBeforeParse?: () => void;
}

export function useCsvImport(opts?: UseCsvImportOptions): UseCsvImportReturn {
  const [csvRows, setCsvRows] = useState<Array<Record<string, string>> | null>(
    null
  );
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<CsvImportMapping | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  function parseFile(file: File) {
    opts?.onBeforeParse?.();
    setParseError(null);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      delimitersToGuess: [",", ";", "\t", "|"],
      complete: (results) => {
        if (results.errors.length > 0) {
          setParseError(results.errors[0]!.message);
          return;
        }
        const fields = (results.meta.fields ?? []).filter(Boolean) as string[];
        if (fields.length === 0) {
          setParseError("Keine Spalten-Header gefunden.");
          return;
        }
        setHeaders(fields);
        setMapping(csvAutoMap(fields));
        setCsvRows(results.data);
      },
      error: (err: Error) => setParseError(err.message),
    });
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0];
    if (f) parseFile(f);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  }

  function reset() {
    setCsvRows(null);
    setHeaders([]);
    setMapping(null);
    setParseError(null);
  }

  return {
    csvRows,
    headers,
    mapping,
    setMapping,
    parseError,
    setParseError,
    dragging,
    setDragging,
    parseFile,
    handleFileInput,
    handleDrop,
    reset,
  };
}
